import React, { useState } from "react";
import styles from './Menu.module.scss';
import { Link, useLocation, useNavigate } from "react-router-dom";
import Icon from "../icon/Icon";
import Label from "../label/Label";
import { useAuth } from "../../contexts/AuthContext";
import PropTypes from "prop-types";
import Modal from "../modal/Modal";
import Button from "../button/Button";

const Menu = ({
  type = 'main',
  isDropdown = false,
  onClick
}) => {
  const [showModal, setShowModal] = useState(false);
  const [openSubMenu, setOpenSubMenu] = useState(null); // Track open submenu
  const { authenticatedUser, doLogout } = useAuth();
  const location = useLocation();
  const activeLink = location.pathname;
  const profileCompleted = authenticatedUser?.profilePicture ?? null;
  const navigate = useNavigate();
  const { role, can } = useAuth();

  let items = [];
  if (type === 'profile') {
    items = [
      {
        text: 'Update Profile',
        to: '/profile',
        icon: 'user',
        label: {
          text: profileCompleted ? '100% Completed' : '80% Completed',
          type: profileCompleted ? 'success' : 'warning'
        }
      },
      {
        text: 'Change Password',
        to: '/change-password',
        icon: 'lock',
      },
      {
        text: 'Logout',
        onClick: (e) => {
          e.stopPropagation();
          setShowModal(true);
        },
        icon: 'logOut'
      },
    ];
  } else {
    items = [
      {
        text: 'Home',
        to: '/',
        icon: 'home',
        roles: ['Instructor', 'Student', 'Program Director', 'Program Coordinator', 'Application Admin']
      },
      {
        text: 'Schedules',
        to: '/schedules',
        icon: 'calendar',
        roles: ['Student']
      },
      {
        text: 'Evaluations',
        to: '/evaluations',
        icon: 'clipboard',
        roles: ['Instructor', 'Student', 'Program Director']
      },
      {
        text: 'Evaluations',
        to: '/evaluations',
        icon: 'clipboard',
        roles: ['Program Coordinator'],
        permission: 'canConfirmEvaluations'
      },
      {
        text: 'Students',
        to: '/students',
        icon: 'users',
        roles: ['Instructor'],
      },
      {
        text: 'Schedule',
        to: '/schedules',
        icon: 'calendar',
        roles: ['Instructor'],
      },
      {
        text: 'Programs',
        to: '/programs',
        icon: 'flag',
        roles: ['Program Director', 'Program Coordinator']
      },
      {
        text: 'User Management',
        roles: ['Program Director'],
        children: [ // Add children for sub-menu
          {
            text: 'Students',
            to: '/students',
            icon: 'students',
          },
          {
            text: 'Instructors',
            to: '/instructors',
            icon: 'users',
          },
          {
            text: 'Clinical Coordinators',
            to: '/clinical-coordinators',
            icon: 'users',
          },
          {
            text: 'Program Coordinators',
            to: '/program-coordinators',
            icon: 'users',
          },
        ]
      },
      {
        text: 'Students',
        to: '/students',
        icon: 'students',
        roles: ['Program Coordinator']
      },
      {
        text: 'Case Logs',
        icon: 'file-text',
        roles: ['Student'],
        children: [
          {
            text: 'Log Reports',
            to: '/log-reports',
            icon: 'file-text',
          },
          {
            text: 'Case Log Calendar',
            to: '/log-reports/calendar',
            icon: 'calendar',
          },
        ]
      },
      {
        text: 'Case Logs',
        roles: ['Program Director'],
        children: [
          {
            text: 'Case Logs',
            to: '/log-reports',
            icon: 'clipboard',
          },
          {
            text: 'Settings',
            to: '/case-log-settings',
            icon: 'settings',
          },
        ]
      },
      {
        text: 'Schedule',
        to: '/schedule',
        icon: 'calendar',
        roles: ['Program Director', 'Program Coordinator']
      },
      {
        text: 'Health Systems',
        to: '/health-systems',
        icon: 'clinic',
        roles: ['Program Director']
      },
      {
        text: 'Clinical Site',
        to: '/clinical-site',
        icon: 'clinic',
        roles: ['Program Director']
      },
      {
        text: 'Evaluation Settings',
        roles: ['Program Director'],
        children: [
          { text: 'Questions', to: '/evaluation-questions', icon: 'clipboard' },
          { text: 'Options', to: '/evaluation-options', icon: 'clipboard' },
        ]
      },
      {
        text: 'Surveys',
        to: '/surveys',
        icon: 'file-text',
        roles: ['Program Director', 'Student']
      },
      {
        text: 'Student Ranking',
        to: '/student-ranking',
        icon: 'award',
        roles: ['Program Director']
      },
      {
        text: 'Student Graduation',
        to: '/student-graduation',
        icon: 'graduation',
        roles: ['Program Director']
      },
      {
        text: 'Employer Details',
        to: '/employer-details',
        icon: 'users',
        roles: ['Program Director']
      },
      {
        text: 'Reports',
        to: '/reports',
        icon: 'file-text',
        roles: ['Student', 'Program Director']
      },
      {
        text: 'Reports',
        to: '/reports',
        icon: 'file-text',
        roles: ['Instructor'],
      },
      {
        text: 'Subscriptions',
        to: '/subscriptions',
        icon: 'file-text',
        roles: ['Application Admin']
      },
      {
        text: 'User Management',
        roles: ['Application Admin'],
        children: [
          { text: 'Program Directors', to: '/program-directors', icon: 'users' },
        ]
      },
      {
        text: 'Continuing Ed. Submission',
        to: 'https://about.cmefy.com/',
        icon: 'external-link',
        roles: ['Instructor'],
        target: "_blank"
      },
      {
        text: 'Accreditation',
        to: 'https://maestrostoragedev.blob.core.windows.net/maestro/files/annual_reporting_suite.xlsx',
        icon: 'award',
        roles: ['Program Director'],
        download: "annual_reporting_suite.xlsx"
      },
      {
        text: 'Support',
        to: 'https://logicleap-tech.atlassian.net/servicedesk/customer/portals',
        icon: 'message-circle',
        roles: ['Instructor', 'Student', 'Program Director'],
        target: "_blank"
      }
    ];
  }

  const handleItemClick = (e, item) => {
    if (item.onClick) {
      item.onClick(e);
    } else if (item.children) {
      e.preventDefault(); // Prevent navigation for parent
      setOpenSubMenu(openSubMenu === item ? null : item); // Toggle submenu
    } else if (onClick) {
      onClick();
    }
  };

  return (
    <>
      <nav className={`${styles.menu} ${isDropdown ? styles.dropdown : ''}`}>
        <ul>
          {items.map((item, index) => {
            if (item.roles && !item.roles.includes(role)) {
              return null;
            }
            if (item.permission && !can(item.permission)) {
              return null;
            }
            const hasChildren = item.children && item.children.length > 0;
            
            // For items with children (submenus), don't mark them as active based on path
            let isActive = false;
            if (!hasChildren) {
              isActive = item.to === '/' ? activeLink === '/' :
                item.to && activeLink.startsWith(item.to);
            }

            return (
              <li key={`menu-${index}`}>
                <Link
                  to={item.to}
                  className={`${isActive ? styles.active : ''} ${hasChildren ? styles.hasChildren : ''}`}
                  onClick={(e) => handleItemClick(e, item)}
                  target={item.target || undefined}
                  download={item.download || undefined}
                >
                  {item.icon && <Icon name={item.icon} />}
                  <span>{item.text}</span>
                  {item.label && <Label {...item.label} />}
                </Link>
                {hasChildren && (
                  <ul className={styles.subMenu}>
                    {item.children.map((child, childIndex) => {
                      // Exact path matching only
                      const isChildActive = child.to === activeLink;
                      
                      return (
                        <li key={`child-${childIndex}`}>
                          <Link
                            to={child.to}
                            className={isChildActive ? styles.active : ''}
                            onClick={onClick}
                          >
                            {child.icon && <Icon name={child.icon} />}
                            {child.text}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </nav>
      {showModal && (
        <Modal>
          <div className={styles.logout}>
            <h2 className={styles.modalTitle}>Are you sure you want to log out?</h2>
            <div className={styles.buttons}>
              <Button
                label="Logout"
                size="small"
                variant="secondary"
                onClick={() => {
                  if (onClick) onClick();
                  doLogout();
                  navigate('/login');
                }}
              />
              <Button
                label="Cancel"
                size="small"
                variant="cancel"
                onClick={() => setShowModal(false)}
              />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

Menu.propTypes = {
  type: PropTypes.oneOf(['main', 'profile']),
  isDropdown: PropTypes.bool,
  onClick: PropTypes.func
};

export default Menu;